import React, { useEffect, useState } from 'react';

import { FormContainer } from '@eika-kortbetjening/form-container';
import { CustomerInfo } from '@eika-kortbetjening/order-card-form-confirm/lib/types';
import { CardWillBeSentToAddress } from '@eika-kortbetjening/order-card-form-confirm/src/card-will-be-sent-to-address/CardWillBeSentToAddress';
import { ErrorCard, SuccessCard, SummaryCard } from '@eika/card';
import { Loader } from '@eika/loader';
import { Modal } from '@eika/modal';
import { RichIcon, RichIconTypes } from '@eika/rich-icon';

import { useOrderCard } from '@/react-query/hooks/mutation/use-order-card';
import { useAccountFromQueryParam } from '@/react-query/hooks/query/use-account';
import { useCustomerFromAccount } from '@/react-query/hooks/query/use-customer';

import { useSession } from '@components/app/Session';
import Footer from '@components/footer/Footer';
import { useNavigate } from '@domain/app';
import { usePinChangedQuery } from '@hooks/query/use-pin-changed-query';
import useText from '@hooks/use-text';
import { formatAccountNumber, getAccountName, getAccountNameUserCreated } from '@utils/account-utils';
import { formatPhoneNumber } from '@utils/text-format-utils';
import { goToRoot } from '@utils/url-utils';

import './OrderCardFormSummaryContainer.scss';

export const OrderCardFormSummaryContainer = () => {
    const text = useText();
    const navigate = useNavigate();
    const [showModal, setShowModal] = React.useState<boolean>(false);
    const [casRefresh, setCasRefresh] = useState(false);
    const session = useSession();

    const pinChanged = usePinChangedQuery();
    const accountQuery = useAccountFromQueryParam();
    const userQuery = useCustomerFromAccount(accountQuery.data, casRefresh);

    const isAutomaticDebitCardOrderEnabled =
        (session.toggles.AUTOMATIC_DEBIT_CARD_REORDER ?? false) ||
        (session.debitProfile?.allowedOperations.cardAutomaticOrder ?? false);

    useEffect(() => {
        if (userQuery.isError) {
            setCasRefresh(true);
        }
    }, [userQuery.isError]);

    useEffect(() => {
        const handleMessage = (event) => {
            if (event.data === 'close-modal') {
                setShowModal(false);
            }
        };

        window.addEventListener('message', handleMessage);

        // Cleanup the event listener on component unmount
        return () => {
            window.removeEventListener('message', handleMessage);
        };
    }, [accountQuery.data]);

    const orderCardMutation = useOrderCard(accountQuery.data?.number ?? '', !isAutomaticDebitCardOrderEnabled);

    if (orderCardMutation.data?.needsSign === false) {
        goToRoot(navigate);
    }

    if (accountQuery.isPending) {
        return <Loader />;
    }

    if (accountQuery.isError) {
        return null;
    }

    const next = () => {
        setShowModal(true);
        orderCardMutation.mutate({ accountNumber: accountQuery.data.number, changePin: pinChanged });
    };

    const modalNext = () => {
        setShowModal(false);
        goToRoot(navigate);
    };

    const customerInfo: CustomerInfo = {
        formattedConfirmBySmsPhoneNumber: userQuery.data?.contact.mobile
            ? formatPhoneNumber(userQuery.data.contact.mobile)
            : undefined,
        customerAddress: userQuery.data?.address,
    };
    return (
        <FormContainer>
            <FormContainer.Header title={text.orderCardConfirmationTitle} />
            <FormContainer.Content>
                <SummaryCard icon={RichIconTypes.REGNING_M_KAFFEKOPP}>
                    <section className="summary-info-card">
                        <p>
                            {text.orderCardSigningPriorSummaryTextAccount}{' '}
                            {getAccountNameUserCreated(accountQuery.data) ?? getAccountName(accountQuery.data) ?? ''}
                            <span className="bold"> ({formatAccountNumber(accountQuery.data.number)})</span>
                        </p>
                        <p>
                            {text.orderCardSigningPriorSummaryTextSMS}{' '}
                            <span className="bold">{userQuery.data?.contact.mobile}</span>
                        </p>
                        <CardWillBeSentToAddress customerInfo={customerInfo} dictionary={text} />
                    </section>
                </SummaryCard>
                <Modal
                    setOpen={setShowModal}
                    open={showModal}
                    hideCloseButton
                    onClose={modalNext}
                    footerContent={<Footer next={modalNext} canCancel={false} nextText={text.goBackToCards} />}
                >
                    {orderCardMutation.isPending && <Loader />}
                    {orderCardMutation.isSuccess && isAutomaticDebitCardOrderEnabled && (
                        <div className="signing-modal-wrapper">
                            <h2>{text.signingDocument}</h2>
                            <iframe
                                title={text.signingDocument}
                                src={orderCardMutation.data?.order?.url}
                                sandbox="allow-same-origin allow-scripts allow-forms allow-popups"
                                height="325"
                                width="325"
                            />
                        </div>
                    )}
                    {orderCardMutation.isSuccess && !isAutomaticDebitCardOrderEnabled && (
                        <SuccessCard centeredWithMargin title={text.cardOrderProgressOrdered} />
                    )}
                    {orderCardMutation.isError && (
                        <ErrorCard
                            isModal
                            title={text.messageErrorText}
                            description={text.orderCardErrorText}
                            icon={<RichIcon type={RichIconTypes.HJERTE_M_PLASTER} />}
                        />
                    )}
                </Modal>
            </FormContainer.Content>
            <FormContainer.Footer>
                <Footer
                    next={next}
                    canGoBack
                    nextText={isAutomaticDebitCardOrderEnabled ? text.signAndOrder : text.orderDebetCardConfirmation}
                />
            </FormContainer.Footer>
        </FormContainer>
    );
};
